import React from 'react'
import {FormattedDate, FormattedNumber} from 'react-intl'

import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  freeCancellationPenalty,
  isPenaltyExpired,
  RefundType
} from '@vio/core/src/offer/business/cancellationPenalties'
import {formatTimeZone} from '@vio/core/src/offer/business/cancellationPenalties'
import {CancellationPenalty} from '@vio/core/src/offer/types/offer'

import {Price} from '../../search/Price'
import {PenaltyContainer} from './PenaltyContainer'
import {
  currencyConsistentChecker,
  getPenaltyDateTime,
  NUMBER_OF_DECIMALS_FOR_PRICE
} from './utils'

interface Props {
  id: string | number
  penalty: CancellationPenalty
  totalOfferPrice: number
  displayCurrencyCode: string
  hotelTimeZone?: string
  hasChargeLaterRefundPolicy?: boolean
  currencyStyle?: 'symbol' | 'code'
  isAuthenticateLater?: boolean | null
}

export const PenaltyRowItem = ({
  id,
  penalty,
  totalOfferPrice,
  hotelTimeZone,
  hasChargeLaterRefundPolicy,
  displayCurrencyCode,
  currencyStyle,
  isAuthenticateLater
}: Props) => {
  const {end: penaltyEndDate} = penalty
  const isExpired = isPenaltyExpired(penalty)

  const dateTime = getPenaltyDateTime(penaltyEndDate, hotelTimeZone)
  const hasFreeCancellation = freeCancellationPenalty(penalty)

  const penaltyRefundType = hasFreeCancellation
    ? RefundType.freeCancellation
    : RefundType.partialRefund

  const {amount, currency} = penalty
  const isCurrencyConsistent = currencyConsistentChecker(
    currency,
    displayCurrencyCode
  )

  return (
    <PenaltyContainer
      id={`penalty-${id}`}
      date={
        <FormattedMessageWrapper
          defaultMessage="Before {date}"
          id="beforeDeadline"
          values={{
            date: (
              <FormattedDate
                value={dateTime.date}
                month="short"
                day="2-digit"
                year="numeric"
              />
            )
          }}
        />
      }
      amount={
        <Price
          value={Number(amount)}
          currency={currency}
          currencyDisplayVariant={currencyStyle}
          size="md"
          variant="regular"
          maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
          minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE}
        />
      }
      refundType={penaltyRefundType}
      timezone={formatTimeZone(dateTime.timeZone)}
      refundAmount={
        isCurrencyConsistent ? (
          <FormattedNumber
            value={Number(totalOfferPrice - Number(amount))}
            style="currency"
            currencyDisplay={currencyStyle}
            currency={currency}
          />
        ) : null
      }
      dateTime={dateTime.date}
      hasChargeLaterRefundPolicy={hasChargeLaterRefundPolicy}
      isExpired={isExpired}
      isAuthenticateLater={isAuthenticateLater}
    />
  )
}
